import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import {
  askAssistant,
  listAssistantChats,
  listAssistantProviders,
  AssistantChatHistoryItem,
  AssistantProvider,
  AssistantSearchResult,
} from '../api/assistantApi';
import { useProject } from '../context/ProjectContext';
import { useTheme } from '../hooks/useTheme';
import {
  getAssistantProviderId,
  getAssistantSelectedProjectIds,
  hasAssistantSelectedProjectIds,
  setAssistantProviderId,
  setAssistantSelectedProjectIds,
  setHomeMode,
} from '../utils/assistantPreferences';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';

type ResultRow = AssistantSearchResult & {
  projectName: string;
};

type ChatTurn = {
  id: string;
  query: string;
  answerText: string;
  results: ResultRow[];
  errors: string[];
  usedLLM: boolean;
  fallbackReason?: string;
  pending: boolean;
};

const ASSISTANT_PLACEHOLDER = 'Выберите пространство и задайте свой запрос';

export default function AssistantHomePage() {
  const { projects, setCurrentProject, loading } = useProject();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const initializedRef = useRef(false);
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [providers, setProviders] = useState<AssistantProvider[]>([]);
  const [providerId, setProviderId] = useState('');
  const [providersLoading, setProvidersLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [history, setHistory] = useState<AssistantChatHistoryItem[]>([]);
  const [searching, setSearching] = useState(false);

  const isDark = theme.name === 'dark';
  const pageBackground = isDark ? '#0f1419' : '#f5f7fa';
  const surface = isDark ? '#151b23' : '#ffffff';
  const surfaceSoft = isDark ? '#111827' : '#f8fafc';
  const border = isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.28)';
  const mutedText = isDark ? '#94a3b8' : '#64748b';
  const textColor = theme.toolbar.text;
  const hasChat = turns.length > 0;

  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const selectedProjects = useMemo(
    () => selectedIds.map((id) => projectById.get(id)).filter(Boolean),
    [projectById, selectedIds],
  );

  useEffect(() => {
    if (loading || initializedRef.current) return;
    const projectIds = projects.map((p) => p.id);
    const saved = hasAssistantSelectedProjectIds()
      ? getAssistantSelectedProjectIds().filter((id) => projectIds.includes(id))
      : projectIds;
    setSelectedIds(saved);
    initializedRef.current = true;
  }, [loading, projects]);

  useEffect(() => {
    if (!initializedRef.current) return;
    setAssistantSelectedProjectIds(selectedIds);
  }, [selectedIds]);

  useEffect(() => {
    let mounted = true;
    setProvidersLoading(true);
    listAssistantProviders()
      .then((response) => {
        if (!mounted) return;
        const configuredProviders = response.providers.filter((provider) => provider.configured);
        setProviders(configuredProviders);
        const saved = getAssistantProviderId();
        const nextProvider = configuredProviders.some((provider) => provider.id === saved)
          ? saved
          : (configuredProviders.find((provider) => provider.id === 'default') || configuredProviders[0])?.id || '';
        setProviderId(nextProvider);
        setAssistantProviderId(nextProvider);
      })
      .catch(() => {
        if (!mounted) return;
        setProviders([]);
        setProviderId('');
      })
      .finally(() => {
        if (mounted) setProvidersLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    listAssistantChats()
      .then((response) => {
        if (mounted) setHistory(response.chats.slice(0, 10));
      })
      .catch(() => {
        if (mounted) setHistory([]);
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!chatScrollRef.current) return;
    chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [turns]);

  const changeProvider = (nextProviderId: string) => {
    setProviderId(nextProviderId);
    setAssistantProviderId(nextProviderId);
  };

  const toggleProject = (projectId: string) => {
    setSelectedIds((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId],
    );
  };

  const handleSearch = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed || selectedIds.length === 0) return;

    const turnId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const selectedAtSubmit = [...selectedIds];
    const providerAtSubmit = providerId;
    setTurns((prev) => [
      ...prev,
      {
        id: turnId,
        query: trimmed,
        answerText: '',
        results: [],
        errors: [],
        usedLLM: false,
        pending: true,
      },
    ]);
    setQuery('');
    setSearching(true);
    try {
      const response = await askAssistant(selectedAtSubmit, trimmed, 12, providerAtSubmit);
      const nextResults = response.results.map((result) => ({
        ...result,
        projectName: projectById.get(result.artifact.project_id)?.name || result.artifact.project_id,
      })).sort((a, b) => b.score - a.score);
      setTurns((prev) => prev.map((turn) => (
        turn.id === turnId
          ? {
              ...turn,
              answerText: response.answer_text || fallbackAssistantText(nextResults.length),
              results: nextResults,
              usedLLM: response.used_llm,
              fallbackReason: response.fallback_reason,
              pending: false,
            }
          : turn
      )));
      setHistory((prev) => [{
        id: response.chat_id || turnId,
        query: trimmed,
        answer_text: response.answer_text || fallbackAssistantText(nextResults.length),
        project_ids: selectedAtSubmit,
        results: response.results,
        used_llm: response.used_llm,
        provider_id: response.provider_id,
        fallback_reason: response.fallback_reason,
        token_usage: response.token_usage,
        created_at: new Date().toISOString(),
      }, ...prev].slice(0, 10));
    } catch {
      setTurns((prev) => prev.map((turn) => (
        turn.id === turnId
          ? {
              ...turn,
              answerText: 'Не удалось выполнить запрос.',
              errors: ['assistant'],
              pending: false,
            }
          : turn
      )));
    } finally {
      setSearching(false);
    }
  };

  const openSpaces = () => {
    setHomeMode('spaces');
    navigate('/spaces');
  };

  const openArtifact = (result: ResultRow) => {
    const project = projectById.get(result.artifact.project_id);
    if (project) setCurrentProject({ ...project, role: project.role || 'member' });
    navigate(result.artifact.app_path);
  };

  const restoreHistory = (item: AssistantChatHistoryItem) => {
    const restoredResults = item.results.map((result) => ({
      ...result,
      projectName: projectById.get(result.artifact.project_id)?.name || result.artifact.project_id,
    }));
    setTurns([{
      id: item.id,
      query: item.query,
      answerText: item.answer_text || '',
      results: restoredResults,
      errors: [],
      usedLLM: item.used_llm,
      fallbackReason: item.fallback_reason,
      pending: false,
    }]);
    if (item.project_ids?.length) {
      setSelectedIds(item.project_ids.filter((id) => projectById.has(id)));
    }
  };

  const typeLabel = (type: string) => {
    if (type === 'diagram') return 'Диаграмма';
    if (type === 'registry_entry') return 'Запись';
    return type;
  };

  const providerName = (provider: AssistantProvider) => provider.model || provider.label || provider.id;

  const composer = (
    <Box component="form" onSubmit={handleSearch} sx={{ width: '100%', maxWidth: 880, mx: 'auto' }}>
      <TextField
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={ASSISTANT_PLACEHOLDER}
        fullWidth
        size="medium"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end" sx={{ ml: 1, gap: 0.75 }}>
              <Box
                component="select"
                aria-label="Модель поиска"
                value={providerId}
                disabled={providersLoading || providers.length === 0 || searching}
                onChange={(event) => changeProvider(event.target.value)}
                sx={{
                  height: 34,
                  maxWidth: { xs: 126, sm: 190 },
                  borderRadius: '8px',
                  border: `1px solid ${border}`,
                  background: surface,
                  color: textColor,
                  px: 1,
                  fontSize: 13,
                  fontWeight: 700,
                  outline: 'none',
                }}
              >
                {providers.length === 0 && (
                  <option value="">Локальный поиск</option>
                )}
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {providerName(provider)}
                  </option>
                ))}
              </Box>
              <Tooltip title="Отправить">
                <span>
                  <IconButton
                    type="submit"
                    aria-label="Отправить сообщение"
                    disabled={searching || !query.trim() || selectedIds.length === 0}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      background: '#2563eb',
                      color: '#ffffff',
                      '&:hover': { background: '#1d4ed8' },
                      '&.Mui-disabled': {
                        background: isDark ? 'rgba(148, 163, 184, 0.16)' : 'rgba(148, 163, 184, 0.28)',
                        color: mutedText,
                      },
                    }}
                  >
                    {searching ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            minHeight: 56,
            borderRadius: '8px',
            background: surface,
            color: textColor,
            pr: 0.75,
          },
          '& .MuiOutlinedInput-input': {
            minWidth: 0,
          },
        }}
      />

      <Box sx={{ mt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: hasChat ? 'flex-start' : 'center' }}>
        {projects.map((project) => {
          const selected = selectedIds.includes(project.id);
          return (
            <Chip
              key={project.id}
              size="small"
              clickable
              label={project.name}
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              onClick={() => toggleProject(project.id)}
              sx={{
                borderRadius: '8px',
                maxWidth: 180,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );

  return (
    <>
      <Header title="Aspect" height={56} />
      <Box sx={{ position: 'relative', height: '100vh', pt: '56px', background: pageBackground, color: textColor, boxSizing: 'border-box' }}>
        {!hasChat && (
          <Button
            variant="outlined"
            startIcon={<FolderOutlinedIcon />}
            onClick={openSpaces}
            sx={{
              position: 'absolute',
              top: 72,
              right: { xs: 16, md: 24 },
              zIndex: 3,
              borderRadius: '8px',
              borderColor: border,
              color: textColor,
              backgroundColor: surface,
              textTransform: 'none',
              fontWeight: 800,
              '&:hover': {
                borderColor: '#2563eb',
                backgroundColor: isDark ? 'rgba(37, 99, 235, 0.12)' : 'rgba(37, 99, 235, 0.08)',
              },
            }}
          >
            Пространства
          </Button>
        )}
        <Box
          sx={{
            height: '100%',
            display: 'grid',
            gridTemplateColumns: hasChat ? { xs: '1fr', lg: 'minmax(0, 1fr) 300px' } : '1fr',
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 2,
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {hasChat ? (
              <>
                <Box
                  role="toolbar"
                  aria-label="Панель ассистента"
                  sx={{
                    border: `1px solid ${border}`,
                    background: surface,
                    borderRadius: '8px',
                    px: 1.5,
                    py: 1,
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: textColor, fontWeight: 800, fontSize: 16 }}>Aspect</Typography>
                    <Typography sx={{ color: mutedText, fontSize: 12 }}>
                      {selectedProjects.length} пространств
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<FolderOutlinedIcon />}
                      onClick={openSpaces}
                      sx={{
                        borderRadius: '8px',
                        borderColor: border,
                        color: textColor,
                        textTransform: 'none',
                        fontWeight: 800,
                        '&:hover': {
                          borderColor: '#2563eb',
                          backgroundColor: isDark ? 'rgba(37, 99, 235, 0.12)' : 'rgba(37, 99, 235, 0.08)',
                        },
                      }}
                    >
                      Пространства
                    </Button>
                    <Tooltip title="Профиль">
                      <IconButton size="small" onClick={() => navigate('/profile')} sx={{ color: textColor }}>
                        <SettingsOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box
                  ref={chatScrollRef}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    pr: { xs: 0, md: 0.5 },
                  }}
                >
                  {turns.map((turn) => (
                    <ChatTurnView
                      key={turn.id}
                      turn={turn}
                      border={border}
                      surface={surface}
                      surfaceSoft={surfaceSoft}
                      textColor={textColor}
                      mutedText={mutedText}
                      typeLabel={typeLabel}
                      openArtifact={openArtifact}
                    />
                  ))}
                </Box>

                <Box sx={{ pt: 1.5 }}>
                  {composer}
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  px: 1,
                }}
              >
                <Typography component="h1" sx={{ color: textColor, fontWeight: 900, fontSize: { xs: 38, md: 52 }, letterSpacing: 0, mb: 0.75 }}>
                  Aspect
                </Typography>
                <Typography sx={{ color: mutedText, fontSize: 16, mb: 2.5, textAlign: 'center' }}>
                  {ASSISTANT_PLACEHOLDER}
                </Typography>
                {composer}
              </Box>
            )}
          </Box>

          {(hasChat || history.length > 0) && (
            <Box
              sx={{
                minHeight: 0,
                overflowY: 'auto',
                border: `1px solid ${border}`,
                background: surface,
                borderRadius: '8px',
                p: 1.5,
                display: { xs: hasChat ? 'none' : 'block', lg: 'block' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <HistoryOutlinedIcon sx={{ fontSize: 18, color: '#2563eb' }} />
                <Typography sx={{ color: textColor, fontSize: 14, fontWeight: 800 }}>История</Typography>
              </Box>
              {history.length === 0 ? (
                <Typography sx={{ color: mutedText, fontSize: 13 }}>Последние запросы появятся здесь.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {history.map((item) => (
                    <Box
                      key={item.id}
                      component="button"
                      type="button"
                      onClick={() => restoreHistory(item)}
                      sx={{
                        textAlign: 'left',
                        border: `1px solid ${border}`,
                        background: surfaceSoft,
                        color: textColor,
                        borderRadius: '8px',
                        p: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: '#2563eb',
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.query}
                      </Typography>
                      {item.answer_text && (
                        <Typography sx={{ mt: 0.5, color: mutedText, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.answer_text}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}

function ChatTurnView({
  turn,
  border,
  surface,
  surfaceSoft,
  textColor,
  mutedText,
  typeLabel,
  openArtifact,
}: {
  turn: ChatTurn;
  border: string;
  surface: string;
  surfaceSoft: string;
  textColor: string;
  mutedText: string;
  typeLabel: (type: string) => string;
  openArtifact: (result: ResultRow) => void;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ alignSelf: 'flex-end', maxWidth: { xs: '100%', md: '72%' }, background: '#2563eb', color: '#ffffff', borderRadius: '8px', px: 1.5, py: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{turn.query}</Typography>
      </Box>

      <Box sx={{ alignSelf: 'flex-start', width: '100%', maxWidth: 920, border: `1px solid ${border}`, background: surface, borderRadius: '8px', p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: turn.pending ? 0 : 1 }}>
          <SmartToyOutlinedIcon sx={{ color: '#2563eb', fontSize: 18 }} />
          <Typography sx={{ color: textColor, fontWeight: 800, fontSize: 14 }}>Ассистент</Typography>
        </Box>

        {turn.pending ? (
          <Box aria-label="Ассистент печатает" sx={{ display: 'flex', gap: 0.5, py: 0.75, '@keyframes assistantDot': { '0%, 80%, 100%': { opacity: 0.25, transform: 'translateY(0)' }, '40%': { opacity: 1, transform: 'translateY(-3px)' } } }}>
            {[0, 1, 2].map((dot) => (
              <Box
                key={dot}
                component="span"
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: '#2563eb',
                  animation: 'assistantDot 1.1s infinite ease-in-out',
                  animationDelay: `${dot * 0.14}s`,
                }}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {turn.answerText && (
              <Typography sx={{ color: textColor, fontSize: 14, lineHeight: 1.55 }}>
                {turn.answerText}
              </Typography>
            )}
            {turn.errors.length > 0 && (
              <Typography sx={{ color: '#dc2626', fontSize: 13 }}>
                Ошибка поиска: {turn.errors.join(', ')}
              </Typography>
            )}
            {turn.fallbackReason && (
              <Typography sx={{ color: mutedText, fontSize: 12 }}>
                LLM недоступна или ответ ограничен, показаны результаты локального поиска.
              </Typography>
            )}
            {turn.results.length === 0 && !turn.answerText && (
              <Box sx={{ border: `1px dashed ${border}`, background: surfaceSoft, borderRadius: '8px', p: 1.5, color: mutedText }}>
                Ссылки не найдены
              </Box>
            )}
            {turn.results.map((result) => (
              <Box
                key={`${turn.id}:${result.artifact.project_id}:${result.artifact.type}:${result.artifact.id}`}
                sx={{
                  border: `1px solid ${border}`,
                  background: surfaceSoft,
                  borderRadius: '8px',
                  p: 1.5,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
                    <Chip size="small" label={typeLabel(result.artifact.type)} sx={{ height: 22, borderRadius: '8px' }} />
                    <Chip size="small" label={result.projectName} sx={{ height: 22, borderRadius: '8px' }} />
                  </Box>
                  <Typography sx={{ color: textColor, fontWeight: 800, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {result.artifact.title}
                  </Typography>
                  {result.artifact.subtitle && (
                    <Typography sx={{ color: mutedText, fontSize: 13, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {result.artifact.subtitle}
                    </Typography>
                  )}
                </Box>
                <Button
                  onClick={() => openArtifact(result)}
                  endIcon={<LaunchOutlinedIcon />}
                  sx={{ justifySelf: { xs: 'start', md: 'end' }, borderRadius: '8px', textTransform: 'none', fontWeight: 800 }}
                >
                  Открыть
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function fallbackAssistantText(resultCount: number): string {
  if (resultCount === 0) return 'Ничего не найдено.';
  if (resultCount === 1) return 'Найден 1 артефакт.';
  return `Найдено ${resultCount} артефактов.`;
}
